import { useState, useCallback, useEffect } from 'react'

type ReadStatus = {
  timestamp: number
  onlyLast: boolean
}

type ReadStatusMap = Record<string, ReadStatus>

export function useGroupedNotesReadStatus() {
  const [readStatusMap, setReadStatusMap] = useState<ReadStatusMap>(() => {
    const stored = window.localStorage.getItem('s:groupedNotesReadStatus')
    return stored ? JSON.parse(stored) : {}
  })

  // Persist to localStorage whenever the map changes
  useEffect(() => {
    window.localStorage.setItem('s:groupedNotesReadStatus', JSON.stringify(readStatusMap))
  }, [readStatusMap])

  const markLastNoteRead = useCallback((pubkey: string, newestNoteTimestamp: number) => {
    setReadStatusMap((prev) => ({
      ...prev,
      [pubkey]: {
        timestamp: newestNoteTimestamp,
        onlyLast: true
      }
    }))
  }, [])

  const markAllNotesRead = useCallback((pubkey: string, newestNoteTimestamp: number) => {
    setReadStatusMap((prev) => ({
      ...prev,
      [pubkey]: {
        timestamp: newestNoteTimestamp,
        onlyLast: false
      }
    }))
  }, [])

  const getReadStatus = useCallback(
    (pubkey: string, newestNoteTimestamp: number) => {
      const status = readStatusMap[pubkey]
      if (!status) {
        return { isLastNoteRead: false, areAllNotesRead: false }
      }

      // If the newest note is newer than our stored timestamp, it's unread
      if (newestNoteTimestamp > status.timestamp) {
        return { isLastNoteRead: false, areAllNotesRead: false }
      }

      // Otherwise, check the onlyLast flag
      return {
        isLastNoteRead: true,
        areAllNotesRead: !status.onlyLast
      }
    },
    [readStatusMap]
  )

  const getUnreadCount = useCallback((_pubkey: string, allNoteTimestamps: number[]) => {
    // Always return the actual current count of notes within the timeframe
    return allNoteTimestamps.length
  }, [])

  const markAsUnread = useCallback((pubkey: string) => {
    setReadStatusMap((prev) => {
      const newMap = { ...prev }
      delete newMap[pubkey]
      return newMap
    })
  }, [])

  return {
    markLastNoteRead,
    markAllNotesRead,
    getReadStatus,
    getUnreadCount,
    markAsUnread
  }
}
